import { useEffect, useMemo, useState } from 'react';
import { Stage } from '../../api/issues';
import { getReevaluateOptIns, ReevaluateOptIns, saveReevaluateOptIn } from '../../api/reevaluation';
import { describeError } from '../../api/requeue';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { moment } from './issueFormat';
import { afterDeployLabel, afterDeployRows, withChoice } from './reevaluateOptInModel';

export interface IReevaluateAfterDeployListProps {
    environments: { customer: string; environment: string }[];
    /** The switch at the top of the page: only production, only test, or both. */
    stage?: Stage | '';
}

const inStage = (environment: string, stage?: Stage | '') =>
    !stage || environment.trim().toLowerCase().startsWith('prod') === (stage === 'prod');

/**
 * Per environment whether the rollout watch starts a dry run an hour after a deploy, folded away.
 * The choices are read once; the environments the page refreshes only change which rows show.
 */
export const ReevaluateAfterDeployList = ({ environments, stage }: IReevaluateAfterDeployListProps) => {
    const axios = useAxiosSwagger();
    const [optIns, setOptIns] = useState<ReevaluateOptIns | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!axios) {
            return;
        }

        const controller = new AbortController();

        getReevaluateOptIns(axios, controller.signal)
            .then((read) =>
                setOptIns((shown) =>
                    shown ? read.environments.reduce(withChoice, { ...shown, configured: read.configured }) : read
                )
            )
            .catch((ex) => {
                if (!controller.signal.aborted) {
                    setError(describeError(ex));
                }
            });

        return () => controller.abort();
    }, [axios]);

    const rows = useMemo(
        () => afterDeployRows(environments, optIns).filter((r) => inStage(r.environment, stage)),
        [environments, optIns, stage]
    );

    const change = (customer: string, environment: string, key: string, enabled: boolean) => {
        if (!axios) {
            return;
        }

        setBusy(key);
        setError(null);

        saveReevaluateOptIn(axios, customer, environment, enabled)
            .then((saved) =>
                setOptIns((shown) => withChoice(shown ?? { configured: false, environments: [] }, saved))
            )
            .catch((ex) => setError(describeError(ex)))
            .finally(() => setBusy(null));
    };

    const on = rows.filter((r) => r.on).length;

    return (
        <details className="reevaluation__section">
            <summary className="reevaluation__heading">
                Na deploy: aan bij {on} van {rows.length}
            </summary>
            {error && <p className="issues__error">{error}</p>}
            <div className="reevaluation__after-deploy">
                {rows.map((r) => (
                    <label
                        key={r.key}
                        className="reevaluation__choice form-check"
                        title={
                            r.own && r.changedBy
                                ? `${r.on ? 'Aangezet' : 'Uitgezet'} door ${r.changedBy} op ${moment(r.changedAt)}`
                                : 'Standaard'
                        }
                    >
                        <input
                            type="checkbox"
                            className="form-check-input"
                            checked={r.on}
                            disabled={!optIns || busy !== null}
                            onChange={(event) => change(r.customer, r.environment, r.key, event.target.checked)}
                        />
                        <span>
                            {r.customer} / {r.environment}
                        </span>
                        <span className="issues__muted">
                            {afterDeployLabel(r.on)}
                            {r.own ? '' : ' (standaard)'}
                        </span>
                    </label>
                ))}
            </div>
        </details>
    );
};
